import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, RefreshCw, Image, Type, Layout, Upload, HelpCircle, Plus, Trash2, Instagram, Star, MapPin } from 'lucide-react';
import toast from 'react-hot-toast';

const ImageInput = ({ label, value, onChange, compact = false }) => {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post('/api/admin/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        onChange(response.data.url);
        toast.success('Image uploaded!');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className={`block font-medium text-gray-700 mb-1 ${compact ? 'text-xs' : 'text-sm'}`}>{label}</label>
      <div className="flex gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`flex-1 border rounded-lg focus:ring-[#471F21] focus:border-[#471F21] ${compact ? 'px-3 py-1.5 text-sm' : 'px-4 py-2'}`}
          placeholder="Enter URL or upload"
        />
        <label className={`flex items-center justify-center bg-gray-100 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors ${compact ? 'px-3 py-1.5' : 'px-4 py-2'} ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
          {uploading ? (
            <div className="w-4 h-4 border-2 border-[#471F21] border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <Upload className="w-4 h-4" />
          )}
          <input
            type="file"
            className="hidden"
            accept="image/*"
            onChange={handleUpload}
            disabled={uploading}
          />
        </label>
      </div>
      {value && (
        <img src={value} alt="Preview" className={`mt-2 object-cover rounded-md border border-gray-200 ${compact ? 'h-20' : 'h-32'} w-auto`} />
      )}
    </div>
  );
};

const LandingPageEditor = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState(null);
  const [activeTab, setActiveTab] = useState('hero');

  const tabs = [
    { id: 'hero', label: 'Hero & Vision', icon: Layout },
    { id: 'content', label: 'Offerings & About', icon: Type },
    { id: 'testimonials', label: 'Social & Reviews', icon: Instagram },
    { id: 'support', label: 'FAQs & Map', icon: HelpCircle },
  ];

  const defaultContent = {
    hero: {
      title: "Thaathayya Illu",
      subtitle: "A home built to create memories — rooted in Telugu culture and yesteryear.",
      buttonText: "Book your photoshoot",
      image: "https://images.unsplash.com/photo-1605218427368-35b863773ba9?q=80&w=2070&auto=format&fit=crop"
    },
    about: {
      title: "Step into a home that feels like a memory.",
      description1: "Thaathayya Illu is a thoughtfully created home, built with a single intention: to hold memory, culture, and the quiet rhythms of old Telugu life.",
      description2: "Inspired by traditional homes, courtyards, and everyday rituals of the past, the space is designed to feel familiar and grounding — a place where moments unfold naturally and memories are made with ease.",
      image1: "https://images.unsplash.com/photo-1596627685698-466d6d8d6725?q=80&w=1000&auto=format&fit=crop",
      image2: "https://images.unsplash.com/photo-1629079377484-8846c430e38a?q=80&w=1000&auto=format&fit=crop"
    },
    offerings: [
      {
        title: "Photoshoots & Events",
        description: "For sessions from 1 November 2026, photoshoots start at ₹7,200. Pricing varies by shoot type and total headcount.",
        image: "https://images.unsplash.com/photo-1533227297464-675ad7a47f70?q=80&w=1000&auto=format&fit=crop",
        ctaType: "link",
        link: "/photoshoot",
        linkText: "Book Now"
      },
      {
        title: "Celebrations",
        description: "We turn a narrative into a day. From moonlit dinners to intimate gatherings that feel like home.",
        image: "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?q=80&w=1000&auto=format&fit=crop",
        ctaType: "whatsapp",
        link: "",
        linkText: "Contact Us"
      },
      {
        title: "Signature Events",
        description: "Cultural workshops, art gatherings, and community events hosted by Thaathayya Illu.",
        image: "https://images.unsplash.com/photo-1519671482502-9759101d23f7?q=80&w=1000&auto=format&fit=crop",
        ctaType: "none",
        link: "",
        linkText: ""
      }
    ],
    vision: {
      title: "The Spirit of the Space",
      subtitle: "Our Philosophy",
      image: "https://images.unsplash.com/photo-1545960521-c4238e82a6f7?q=80&w=1000&auto=format&fit=crop",
      cards: [
        { title: "Rooted in Culture", description: "Rooted in Telugu cultural aesthetics and sensibilities, bringing the charm of yesteryears to life.", icon: "Calendar" },
        { title: "Slow Living", description: "Inspired by yesteryear homes, courtyards, and slow living. A pause from the fast-paced world.", icon: "Star" },
        { title: "Storytelling", description: "Calm, uncluttered spaces that let people and moments take centre stage. Designed for emotion and intimacy.", icon: "Camera" },
        { title: "Meaning Over Scale", description: "Ideal for those who value meaning over scale. Perfect for intimate gatherings and soulful captures.", icon: "Users" }
      ]
    },
    testimonials: [
      { name: "Srilekhya H.", role: "Event Host", quote: "Laughter in the courtyards, filter coffee at dusk, games under mango trees - Thaathayya Illu lingers in hearts long after the day ends.", image: "" },
      { name: "Priya M.", role: "Pre-Wedding Shoot", quote: "The perfect backdrop for our love story. Every corner of this home told a tale, and our photos captured it all beautifully.", image: "" },
      { name: "Rajesh K.", role: "Family Reunion", quote: "We celebrated my parents' 50th anniversary here. The warmth of the space made it feel like we were back in our ancestral home.", image: "" }
    ],
    faqs: [
      {
        question: "How do I book a photoshoot?",
        answer: "You can book directly through our website by selecting your preferred date and duration (3, 6, 8, or 12 hours)."
      },
      {
        question: "What are the pricing options?",
        answer: "For sessions from 1 November 2026, tariffs depend on duration and package. A 3-hour couple or family shoot is ₹7,200 (up to 10 people); commercial and Movie / OTT packages are selected during booking."
      },
      {
        question: "Is there a changing room available?",
        answer: "Yes, we provide a dedicated private space for changing and makeup adjustments during your slot."
      },
      {
        question: "What is your cancellation policy?",
        answer: "Cancellations made 48 hours before the slot receive a 50% refund. Cancellations within 48 hours are non-refundable."
      },
      {
        question: "Can we bring props?",
        answer: "Yes, you are welcome to bring props. We request that you ensure they do not cause damage to the property."
      }
    ],
    instagram: {
      profile: {
        username: "thaathayya.illu",
        fullName: "Thaathayya illu",
        bio: "That palleturi illu that our father, mother and we have dreamt of. Mokila, Hyd | Family-operated",
        avatar: "https://instagram.fcok11-1.fna.fbcdn.net/v/t51.2885-19/471012170_1091218556094236_540974868953112444_n.jpg?_nc_ht=instagram.fcok11-1.fna&_nc_cat=109&_nc_ohc=f6Jq_KxL7c8Q7kNvwF_Q4R-&_nc_gid=5319qIRRA7ctdiBLnq7kNQ&edm=AL09I-kBAAAA&ccb=7-5&oh=00_Afv5t_H_Y5zGZB5b9H7_yB-Gz-D_V-J_H_Y5zGZB5b9H7_y&oe=698E64E4&_nc_sid=58cdad",
        stats: {
          posts: 183,
          followers: "12.6K",
          following: 2
        }
      },
      posts: [
        {
          url: "https://www.instagram.com/thaathayya.illu/reel/DUU9bmuk89v/",
          thumbnail: "https://instagram.fcok11-1.fna.fbcdn.net/v/t51.71878-15/626527094_891913760253230_8300642833015949558_n.jpg?stp=dst-jpg_e15_tt6&_nc_cat=100&ig_cache_key=MzgyNDk1MjE2MTM5MTA3MTA4Nw%3D%3D.3-ccb7-5&ccb=7-5&_nc_sid=58cdad&efg=eyJ2ZW5jb2RlX3RhZyI6InhwaWRzLjY0MHgxMTM2LnNkci5DMiJ9&_nc_ohc=7KwbsxEuNWIQ7kNvwH1X7Vc&_nc_oc=AdmvIQ_hfs3XZsSXEXftktrIi4DagcbgyD_YCSEBjQZNauZFxaJWT4MUYpfytotOsLs&_nc_ad=z-m&_nc_cid=0&_nc_zt=23&_nc_ht=instagram.fcok11-1.fna&_nc_gid=5319qIRRA7ctdiBLnq7kNQ&oh=00_AfvgJ0V3jR8MvCAKZFdeCF3CO7K43jSbTk7WMq8gekdddQ&oe=698E5E24",
          type: "video"
        },
        {
          url: "https://www.instagram.com/thaathayya.illu/reel/DUFeHTqCRqu/",
          thumbnail: "https://instagram.fcok11-1.fna.fbcdn.net/v/t51.71878-15/623328739_25755185037455768_1679246121119004365_n.jpg?stp=dst-jpg_e15_tt6&_nc_cat=100&ig_cache_key=MzgyMDU5MjMwMjQzMTczODU0Mg%3D%3D.3-ccb7-5&ccb=7-5&_nc_sid=58cdad&efg=eyJ2ZW5jb2RlX3RhZyI6InhwaWRzLjY0MHgxMTM2LnNkci5DMyJ9&_nc_ohc=9RZadbcD3T8Q7kNvwG7-Iud&_nc_oc=Adnzu1FIBsOxAHl5E-KJgYuZ3vc90-eT6JDjPcDHb5zgIpNaYw4Gh6g5xkWyot20sRk&_nc_ad=z-m&_nc_cid=0&_nc_zt=23&_nc_ht=instagram.fcok11-1.fna&_nc_gid=5319qIRRA7ctdiBLnq7kNQ&oh=00_AfsSVON2cSQfyXNPCOBq982gW0P_jbJzxH03nxemil6HXQ&oe=698E74DC",
          type: "video"
        },
        {
          url: "https://www.instagram.com/thaathayya.illu/reel/DUA7COrEhOs/",
          thumbnail: "https://instagram.fcok11-1.fna.fbcdn.net/v/t51.82787-15/621640812_18155651065442283_2033558308549897685_n.jpg?stp=dst-jpg_e15_tt6&_nc_cat=108&ig_cache_key=MzgxOTMxMjEyMTk0ODI3OTcyNDE4MTU1NjUxMDU5NDQyMjgz.3-ccb7-5&ccb=7-5&_nc_sid=58cdad&efg=eyJ2ZW5jb2RlX3RhZyI6InhwaWRzLjU0MHg5NjAuc2RyLkMzIn0%3D&_nc_ohc=NxsAS13xwJYQ7kNvwFCUPGu&_nc_oc=AdngrV3uz8WBCFvCMlhMswqgtrpaA_KWcY5zJnjo5RHzqlAydVZ2aNkwye0P-k7ZHz4&_nc_ad=z-m&_nc_cid=0&_nc_zt=23&_nc_ht=instagram.fcok11-1.fna&_nc_gid=5319qIRRA7ctdiBLnq7kNQ&oh=00_AfstlKXO0CnuyFqJ6b2OvyrmlZ0CD6kA6YFFbyiaxvwyUw&oe=698E7AC3",
          type: "video"
        },
        {
          url: "https://www.instagram.com/thaathayya.illu/reel/DT4dgMqiZRK/",
          thumbnail: "https://instagram.fcok11-1.fna.fbcdn.net/v/t51.71878-15/619960444_1842418186463071_6493846429391850365_n.jpg?stp=dst-jpg_e15_tt6&_nc_cat=102&ig_cache_key=MzgxNjkzMDQ0MDE2NzEzNDI4Mg%3D%3D.3-ccb7-5&ccb=7-5&_nc_sid=58cdad&efg=eyJ2ZW5jb2RlX3RhZyI6InhwaWRzLjY0MHgxMTM2LnNkci5DMyJ9&_nc_ohc=bBLyocc4GCQ7kNvwEqFERR&_nc_oc=Admj5WPl7AYWr_tvHH8Q4YbR4pzH6wPTGRLPpZ9Os63VJb99UZD3LWX362OMIEotRzQ&_nc_ad=z-m&_nc_cid=0&_nc_zt=23&_nc_ht=instagram.fcok11-1.fna&_nc_gid=5319qIRRA7ctdiBLnq7kNQ&oh=00_AfsOYp9zMRRCjzdskcuUjmJQpgFVUsw0WEfSNEJ0SRr8LQ&oe=698E7642",
          type: "video"
        }
      ]
    },
    location: {
      title: "Find Your Path Home",
      address: "Mokila, Hyderabad, Telangana",
      fullAddress: "Thaathayya Illu, Mokila, Shankarpalli Road, Hyderabad - 501203",
      mapEmbedUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3806.8973642559663!2d78.1903281!3d17.4167125!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcbef0004ca7741%3A0xe04df57a5baf5d67!2sThaathayya%20Illu!5e0!3m2!1sen!2sin!4v1770562582719!5m2!1sen!2sin",
      directionsUrl: "https://maps.app.goo.gl/v6Xm7Tb32v82ePUU6",
      phone: "+91 93982 35164",
      email: "thaathayyaillu@gmail.com",
      whatsappNumber: "919398235164"
    }
  };

  useEffect(() => {
    fetchContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get('/api/admin/settings?id=landing_page', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const fetchedSettings = response.data.settings || {};

        // Deep merge: Replace arrays entirely from fetched data, merge objects
        setContent(prev => {
          const merged = { ...defaultContent };

          Object.keys(fetchedSettings).forEach(key => {
            const fetchedValue = fetchedSettings[key];
            if (fetchedValue === null || fetchedValue === undefined) return;

            if (Array.isArray(fetchedValue)) {
              // Handle arrays (offerings, testimonials, faqs)
              if (fetchedValue.length > 0) {
                if (key === 'offerings') {
                  // Migrate offerings CTAs
                  merged.offerings = fetchedValue.map(offering => {
                    const title = offering.title || '';
                    let ctaType = offering.ctaType;
                    let linkText = offering.linkText;
                    let link = offering.link;

                    // Migration logic for specific sections
                    if (title.toLowerCase().includes('celebration')) {
                      ctaType = 'whatsapp';
                      linkText = 'Contact Us';
                      link = '';
                    } else if (title.toLowerCase().includes('signature')) {
                      ctaType = 'none';
                      linkText = '';
                      link = '';
                    }

                    return { ...offering, ctaType, linkText, link };
                  });
                } else {
                  merged[key] = fetchedValue;
                }
              }
            } else if (typeof fetchedValue === 'object') {
              // Merge object fields (hero, about, vision)
              merged[key] = { ...merged[key] };
              Object.keys(fetchedValue).forEach(subKey => {
                const subValue = fetchedValue[subKey];

                // Special handling for vision.cards migration
                if (key === 'vision' && subKey === 'cards' && Array.isArray(subValue)) {
                  merged.vision.cards = subValue.map(card => ({
                    title: card.title || '',
                    description: card.description || card.text || '',
                    icon: card.icon || 'Star'
                  }));
                } else if (key === 'instagram' && subKey === 'reels' && Array.isArray(subValue)) {
                  // Migrate reels to posts if legacy data exists
                  merged.instagram.posts = subValue.map(reel => ({
                    url: reel.url || '',
                    thumbnail: reel.thumbnail || '',
                    type: 'video'
                  }));
                } else if (subValue !== null && subValue !== undefined && subValue !== '') {
                  merged[key][subKey] = subValue;
                }
              });
            } else if (fetchedValue !== '') {
              merged[key] = fetchedValue;
            }
          });

          return merged;
        });
      } else {
        setContent(defaultContent);
      }
    } catch (error) {
      console.error('Error fetching landing content:', error);
      setContent(defaultContent);
    } finally {
      setLoading(false);
    }
  };

  const saveContent = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put('/api/admin/settings?id=landing_page', content, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Landing page updated successfully!');
    } catch (error) {
      console.error('Error saving landing content:', error);
      toast.error('Failed to update landing page');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (section, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const updateOfferings = (index, field, value) => {
    setContent(prev => {
      const newOfferings = [...prev.offerings];
      newOfferings[index] = { ...newOfferings[index], [field]: value };
      return { ...prev, offerings: newOfferings };
    });
  };

  const updateVisionCard = (index, field, value) => {
    setContent(prev => {
      const newCards = [...prev.vision.cards];
      newCards[index] = { ...newCards[index], [field]: value };
      return {
        ...prev,
        vision: { ...prev.vision, cards: newCards }
      };
    });
  };

  const updateTestimonial = (index, field, value) => {
    setContent(prev => {
      const newTestimonials = [...(prev.testimonials || [])];
      newTestimonials[index] = { ...newTestimonials[index], [field]: value };
      return { ...prev, testimonials: newTestimonials };
    });
  };

  const updateFaq = (index, field, value) => {
    setContent(prev => {
      const newFaqs = [...(prev.faqs || [])];
      newFaqs[index] = { ...newFaqs[index], [field]: value };
      return { ...prev, faqs: newFaqs };
    });
  };

  const addFaq = () => {
    setContent(prev => ({
      ...prev,
      faqs: [...(prev.faqs || []), { question: "New Question", answer: "New Answer" }]
    }));
  };

  const deleteFaq = (index) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;
    setContent(prev => {
      const newFaqs = [...(prev.faqs || [])];
      newFaqs.splice(index, 1);
      return { ...prev, faqs: newFaqs };
    });
  };

  if (loading || !content) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#471F21]"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-100px)] -m-4">
      {/* Header */}
      <div className="flex justify-between items-center p-6 bg-white border-b z-20">
        <div>
          <h2 className="text-2xl font-bold text-[#471F21]">Landing Page Editor</h2>
          <p className="text-sm text-gray-500">Manage your website's primary content and visuals</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={fetchContent}
            className="flex items-center text-gray-600 hover:text-gray-900 px-4 py-2 border rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Reset
          </button>
          <button
            onClick={saveContent}
            disabled={saving}
            className="bg-[#471F21] text-white px-8 py-2 rounded-lg flex items-center hover:bg-[#2d1415] disabled:opacity-50 shadow-lg shadow-[#471F21]/20 transition-all font-semibold"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar Tabs */}
        <div className="w-64 bg-gray-50 border-r overflow-y-auto p-4 space-y-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeTab === tab.id
                  ? 'bg-white text-[#471F21] shadow-sm border border-gray-200 font-bold translate-x-1'
                  : 'text-gray-500 hover:bg-gray-100'
                  }`}
              >
                <Icon className={`w-5 h-5 ${activeTab === tab.id ? 'text-[#471F21]' : 'text-gray-400'}`} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Main Editor Area */}
        <div className="flex-1 overflow-y-auto bg-gray-50/30 p-8">
          <div className="max-w-4xl mx-auto space-y-8 pb-12">

            {activeTab === 'hero' && (
              <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
                {/* Hero Section */}
                <div className="bg-white rounded-2xl shadow-sm p-8 border border-gray-100">
                  <h3 className="text-xl font-bold mb-6 flex items-center text-[#471F21]">
                    <Layout className="w-5 h-5 mr-3" /> Hero Section
                  </h3>
                  <div className="grid gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Main Title</label>
                      <input
                        type="text"
                        value={content.hero.title}
                        onChange={(e) => updateField('hero', 'title', e.target.value)}
                        className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#471F21]/20 focus:border-[#471F21] outline-none transition-all"
                        placeholder="Enter hero title"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Subtitle</label>
                      <textarea
                        value={content.hero.subtitle}
                        onChange={(e) => updateField('hero', 'subtitle', e.target.value)}
                        rows={2}
                        className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#471F21]/20 focus:border-[#471F21] outline-none transition-all resize-none"
                        placeholder="Enter hero subtitle"
                      />
                    </div>
                    <ImageInput
                      label="Background Hero Image"
                      value={content.hero.image}
                      onChange={(url) => updateField('hero', 'image', url)}
                    />
                  </div>
                </div>

                {/* Vision Section */}
                <div className="bg-white rounded-2xl shadow-sm p-8 border border-gray-100">
                  <h3 className="text-xl font-bold mb-6 flex items-center text-[#471F21]">
                    <Star className="w-5 h-5 mr-3" /> Vision & Spirit
                  </h3>
                  <div className="grid gap-6 mb-8">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Section Title</label>
                      <input
                        type="text"
                        value={content.vision.subtitle || content.vision.title}
                        onChange={(e) => updateField('vision', 'subtitle', e.target.value)}
                        className="w-full px-4 py-3 border rounded-xl"
                      />
                    </div>
                    <ImageInput
                      label="Philosophy Featured Image"
                      value={content.vision.image}
                      onChange={(url) => updateField('vision', 'image', url)}
                    />
                  </div>
                  <div className="space-y-4">
                    <h4 className="font-bold text-[#471F21] mb-4">Philosophy Cards</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {content.vision.cards.map((card, index) => (
                        <div key={index} className="p-4 bg-gray-50 rounded-2xl border border-gray-200">
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div>
                              <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Title</label>
                              <input
                                type="text"
                                value={card.title}
                                onChange={(e) => updateVisionCard(index, 'title', e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Icon</label>
                              <select
                                value={card.icon || 'Star'}
                                onChange={(e) => updateVisionCard(index, 'icon', e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg text-sm bg-white"
                              >
                                <option value="Calendar">Calendar</option>
                                <option value="Camera">Camera</option>
                                <option value="Users">Users</option>
                                <option value="Star">Star</option>
                              </select>
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Description</label>
                            <textarea
                              value={card.description || ''}
                              onChange={(e) => updateVisionCard(index, 'description', e.target.value)}
                              rows={2}
                              className="w-full px-3 py-2 border rounded-lg text-sm resize-none"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'content' && (
              <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
                {/* About Section */}
                <div className="bg-white rounded-2xl shadow-sm p-8 border border-gray-100">
                  <h3 className="text-xl font-bold mb-6 flex items-center text-[#471F21]">
                    <Type className="w-5 h-5 mr-3" /> About Section
                  </h3>
                  <div className="grid gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Main Heading</label>
                      <input
                        type="text"
                        value={content.about.title}
                        onChange={(e) => updateField('about', 'title', e.target.value)}
                        className="w-full px-4 py-3 border rounded-xl"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Paragraph 1</label>
                      <textarea
                        value={content.about.description1}
                        onChange={(e) => updateField('about', 'description1', e.target.value)}
                        rows={3}
                        className="w-full px-4 py-3 border rounded-xl resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Paragraph 2</label>
                      <textarea
                        value={content.about.description2}
                        onChange={(e) => updateField('about', 'description2', e.target.value)}
                        rows={3}
                        className="w-full px-4 py-3 border rounded-xl resize-none"
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <ImageInput
                        label="About Image 1"
                        value={content.about.image1}
                        onChange={(url) => updateField('about', 'image1', url)}
                      />
                      <ImageInput
                        label="About Image 2"
                        value={content.about.image2}
                        onChange={(url) => updateField('about', 'image2', url)}
                      />
                    </div>
                  </div>
                </div>

                {/* Offerings */}
                <div className="bg-white rounded-2xl shadow-sm p-8 border border-gray-100">
                  <h3 className="text-xl font-bold mb-6 flex items-center text-[#471F21]">
                    <Image className="w-5 h-5 mr-3" /> Offerings Section
                  </h3>
                  <div className="space-y-8">
                    {content.offerings.map((offering, index) => (
                      <div key={index} className="p-6 bg-gray-50 rounded-2xl border border-gray-200">
                        <h4 className="font-bold text-[#471F21] mb-6 flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-[#471F21] text-white text-[10px] flex items-center justify-center">0{index + 1}</span>
                          Offering Details
                        </h4>
                        <div className="grid gap-6">
                          <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Card Title</label>
                            <input
                              type="text"
                              value={offering.title}
                              onChange={(e) => updateOfferings(index, 'title', e.target.value)}
                              className="w-full px-4 py-3 border rounded-xl"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Description</label>
                            <textarea
                              value={offering.description}
                              onChange={(e) => updateOfferings(index, 'description', e.target.value)}
                              rows={2}
                              className="w-full px-4 py-3 border rounded-xl resize-none"
                            />
                          </div>
                          <div className="grid md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-xs font-bold text-gray-400 uppercase mb-2">CTA Type</label>
                              <select
                                value={offering.ctaType || 'link'}
                                onChange={(e) => updateOfferings(index, 'ctaType', e.target.value)}
                                className="w-full px-3 py-2 border rounded-xl bg-white"
                              >
                                <option value="link">Link</option>
                                <option value="whatsapp">WhatsApp</option>
                                <option value="none">None</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Button Text</label>
                              <input
                                type="text"
                                value={offering.linkText || ''}
                                onChange={(e) => updateOfferings(index, 'linkText', e.target.value)}
                                className="w-full px-3 py-2 border rounded-xl"
                                placeholder="e.g. Book Now"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Target Link</label>
                              <input
                                type="text"
                                value={offering.link || ''}
                                onChange={(e) => updateOfferings(index, 'link', e.target.value)}
                                className="w-full px-3 py-2 border rounded-xl"
                                placeholder="/photoshoot"
                              />
                            </div>
                          </div>
                          <ImageInput
                            label="Card Image"
                            value={offering.image}
                            onChange={(url) => updateOfferings(index, 'image', url)}
                            compact
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'testimonials' && (
              <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
                {/* Instagram Settings */}
                <div className="bg-white rounded-2xl shadow-sm p-8 border border-gray-100">
                  <div className="flex items-center gap-3 mb-8">
                    <Instagram className="w-6 h-6 text-[#471F21]" />
                    <h3 className="text-xl font-bold text-[#471F21]">Instagram Profile Showcase</h3>
                  </div>

                  <div className="space-y-8">
                    {/* Profile Info */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Username</label>
                        <input
                          type="text"
                          value={content.instagram?.profile?.username || ''}
                          onChange={(e) => {
                            const newProfile = { ...(content.instagram?.profile || {}), username: e.target.value };
                            setContent(prev => ({ ...prev, instagram: { ...prev.instagram, profile: newProfile } }));
                          }}
                          className="w-full px-4 py-3 border rounded-xl"
                          placeholder="@thaathayya.illu"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Full Display Name</label>
                        <input
                          type="text"
                          value={content.instagram?.profile?.fullName || ''}
                          onChange={(e) => {
                            const newProfile = { ...(content.instagram?.profile || {}), fullName: e.target.value };
                            setContent(prev => ({ ...prev, instagram: { ...prev.instagram, profile: newProfile } }));
                          }}
                          className="w-full px-4 py-3 border rounded-xl"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Profile Bio</label>
                      <textarea
                        value={content.instagram?.profile?.bio || ''}
                        onChange={(e) => {
                          const newProfile = { ...(content.instagram?.profile || {}), bio: e.target.value };
                          setContent(prev => ({ ...prev, instagram: { ...prev.instagram, profile: newProfile } }));
                        }}
                        rows={2}
                        className="w-full px-4 py-3 border rounded-xl resize-none"
                      />
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Post Count</label>
                        <input
                          type="number"
                          value={content.instagram?.profile?.stats?.posts || 0}
                          onChange={(e) => {
                            const newStats = { ...(content.instagram?.profile?.stats || {}), posts: parseInt(e.target.value) };
                            const newProfile = { ...(content.instagram?.profile || {}), stats: newStats };
                            setContent(prev => ({ ...prev, instagram: { ...prev.instagram, profile: newProfile } }));
                          }}
                          className="w-full px-4 py-3 border rounded-xl shadow-inner bg-gray-50"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Follower Count</label>
                        <input
                          type="text"
                          value={content.instagram?.profile?.stats?.followers || ''}
                          onChange={(e) => {
                            const newStats = { ...(content.instagram?.profile?.stats || {}), followers: e.target.value };
                            const newProfile = { ...(content.instagram?.profile || {}), stats: newStats };
                            setContent(prev => ({ ...prev, instagram: { ...prev.instagram, profile: newProfile } }));
                          }}
                          className="w-full px-4 py-3 border rounded-xl shadow-inner bg-gray-50"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Following Count</label>
                        <input
                          type="number"
                          value={content.instagram?.profile?.stats?.following || 0}
                          onChange={(e) => {
                            const newStats = { ...(content.instagram?.profile?.stats || {}), following: parseInt(e.target.value) };
                            const newProfile = { ...(content.instagram?.profile || {}), stats: newStats };
                            setContent(prev => ({ ...prev, instagram: { ...prev.instagram, profile: newProfile } }));
                          }}
                          className="w-full px-4 py-3 border rounded-xl shadow-inner bg-gray-50"
                        />
                      </div>
                    </div>

                    <ImageInput
                      label="Profile Avatar Image"
                      value={content.instagram?.profile?.avatar || ''}
                      onChange={(url) => {
                        const newProfile = { ...(content.instagram?.profile || {}), avatar: url };
                        setContent(prev => ({ ...prev, instagram: { ...prev.instagram, profile: newProfile } }));
                      }}
                    />

                    <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                      <label className="block text-[#471F21] font-bold mb-6">Grid Gallery Posts (1:1)</label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {(content.instagram?.posts || []).map((post, index) => (
                          <div key={index} className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm relative group">
                            <button
                              onClick={() => {
                                const newPosts = [...content.instagram.posts];
                                newPosts.splice(index, 1);
                                setContent(prev => ({ ...prev, instagram: { ...prev.instagram, posts: newPosts } }));
                              }}
                              className="absolute -top-2 -right-2 bg-red-500 text-white w-8 h-8 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                            <div className="space-y-4">
                              <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Instagram URL</label>
                                <input
                                  type="text"
                                  value={post.url}
                                  onChange={(e) => {
                                    const newPosts = [...content.instagram.posts];
                                    newPosts[index].url = e.target.value;
                                    setContent(prev => ({ ...prev, instagram: { ...prev.instagram, posts: newPosts } }));
                                  }}
                                  className="w-full px-3 py-2 border rounded-lg text-xs"
                                  placeholder="Link to post" />
                              </div>
                              <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Content Type</label>
                                <select
                                  value={post.type || 'image'}
                                  onChange={(e) => {
                                    const newPosts = [...content.instagram.posts];
                                    newPosts[index].type = e.target.value;
                                    setContent(prev => ({ ...prev, instagram: { ...prev.instagram, posts: newPosts } }));
                                  }}
                                  className="w-full px-3 py-2 border rounded-lg text-xs bg-white"
                                >
                                  <option value="image">Still Image</option>
                                  <option value="video">Reel/Video</option>
                                </select>
                              </div>
                              <ImageInput
                                label="Grid Thumbnail"
                                value={post.thumbnail}
                                onChange={(url) => {
                                  const newPosts = [...content.instagram.posts];
                                  newPosts[index].thumbnail = url;
                                  setContent(prev => ({ ...prev, instagram: { ...prev.instagram, posts: newPosts } }));
                                }}
                                compact
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={() => {
                          const newPosts = [...(content.instagram?.posts || []), { url: '', thumbnail: '', type: 'image' }];
                          setContent(prev => ({ ...prev, instagram: { ...prev.instagram, posts: newPosts } }));
                        }}
                        className="mt-6 w-full py-3 border-2 border-dashed border-[#471F21]/20 rounded-2xl text-[#471F21]/60 hover:border-[#471F21] hover:text-[#471F21] hover:bg-white transition-all font-bold flex items-center justify-center gap-2"
                      >
                        <Plus className="w-5 h-5" /> Add Gallery Post
                      </button>
                    </div>
                  </div>
                </div>

                {/* Testimonials */}
                <div className="bg-white rounded-2xl shadow-sm p-8 border border-gray-100">
                  <h3 className="text-xl font-bold mb-6 flex items-center text-[#471F21]">
                    <Plus className="w-5 h-5 mr-3" /> Guest Testimonials
                  </h3>
                  <div className="space-y-6">
                    {(content.testimonials || []).map((testimonial, index) => (
                      <div key={index} className="p-6 bg-gray-50 rounded-2xl border border-gray-200">
                        <div className="grid md:grid-cols-2 gap-6 mb-4">
                          <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Guest Name</label>
                            <input
                              type="text"
                              value={testimonial.name}
                              onChange={(e) => updateTestimonial(index, 'name', e.target.value)}
                              className="w-full px-4 py-3 border rounded-xl"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Tag/Role</label>
                            <input
                              type="text"
                              value={testimonial.role}
                              onChange={(e) => updateTestimonial(index, 'role', e.target.value)}
                              className="w-full px-4 py-3 border rounded-xl"
                            />
                          </div>
                        </div>
                        <div className="mb-4">
                          <label className="block text-xs font-bold text-gray-400 uppercase mb-2">The Quote</label>
                          <textarea
                            value={testimonial.quote}
                            onChange={(e) => updateTestimonial(index, 'quote', e.target.value)}
                            rows={3}
                            className="w-full px-4 py-3 border rounded-xl resize-none"
                          />
                        </div>
                        <ImageInput
                          label="Guest Avatar (Optional)"
                          value={testimonial.image || ''}
                          onChange={(url) => updateTestimonial(index, 'image', url)}
                          compact
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'support' && (
              <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
                {/* Location Section */}
                <div className="bg-white rounded-2xl shadow-sm p-8 border border-gray-100">
                  <h3 className="text-xl font-bold mb-6 flex items-center text-[#471F21]">
                    <MapPin className="w-5 h-5 mr-3" /> Location & Map
                  </h3>
                  <div className="grid gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Section Heading</label>
                      <input
                        type="text"
                        value={content.location?.title || ''}
                        onChange={(e) => updateField('location', 'title', e.target.value)}
                        className="w-full px-4 py-3 border rounded-xl"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Display Address</label>
                      <input
                        type="text"
                        value={content.location?.address || ''}
                        onChange={(e) => updateField('location', 'address', e.target.value)}
                        className="w-full px-4 py-3 border rounded-xl"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Full Legal/Postal Address</label>
                      <textarea
                        value={content.location?.fullAddress || ''}
                        onChange={(e) => updateField('location', 'fullAddress', e.target.value)}
                        rows={2}
                        className="w-full px-4 py-3 border rounded-xl resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Google Maps Embed URL</label>
                      <input
                        type="text"
                        value={content.location?.mapEmbedUrl || ''}
                        onChange={(e) => updateField('location', 'mapEmbedUrl', e.target.value)}
                        className="w-full px-4 py-3 border rounded-xl font-mono text-xs"
                        placeholder="https://www.google.com/maps/embed?..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Google Maps Short Link (Directions)</label>
                      <input
                        type="text"
                        value={content.location?.directionsUrl || ''}
                        onChange={(e) => updateField('location', 'directionsUrl', e.target.value)}
                        className="w-full px-4 py-3 border rounded-xl font-mono text-xs"
                        placeholder="https://maps.app.goo.gl/..."
                      />
                    </div>
                    <div className="border-t border-gray-100 pt-6 mt-2">
                      <h4 className="text-sm font-bold text-[#471F21] mb-4">Contact Details (shown on the landing page)</h4>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Phone (display)</label>
                          <input
                            type="text"
                            value={content.location?.phone || ''}
                            onChange={(e) => updateField('location', 'phone', e.target.value)}
                            className="w-full px-4 py-3 border rounded-xl"
                            placeholder="+91 93982 35164"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                          <input
                            type="email"
                            value={content.location?.email || ''}
                            onChange={(e) => updateField('location', 'email', e.target.value)}
                            className="w-full px-4 py-3 border rounded-xl"
                            placeholder="thaathayyaillu@gmail.com"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">WhatsApp Number</label>
                          <input
                            type="text"
                            value={content.location?.whatsappNumber || ''}
                            onChange={(e) => updateField('location', 'whatsappNumber', e.target.value)}
                            className="w-full px-4 py-3 border rounded-xl"
                            placeholder="919398235164"
                          />
                          <p className="text-xs text-gray-400 mt-1">Digits only, with country code (used for the WhatsApp button).</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* FAQ Section */}
                <div className="bg-white rounded-2xl shadow-sm p-8 border border-gray-100">
                  <div className="flex justify-between items-center mb-8">
                    <h3 className="text-xl font-bold flex items-center text-[#471F21]">
                      <HelpCircle className="w-6 h-6 mr-3" /> Frequently Asked Questions
                    </h3>
                    <button
                      onClick={addFaq}
                      className="flex items-center gap-2 bg-[#F4ECDE] text-[#471F21] px-6 py-2 rounded-xl hover:bg-[#EADBC8] transition-all font-bold"
                    >
                      <Plus className="w-4 h-4" /> Add FAQ
                    </button>
                  </div>
                  <div className="space-y-6">
                    {(content.faqs || []).map((faq, index) => (
                      <div key={index} className="p-6 bg-gray-50 rounded-2xl border border-gray-200 relative group">
                        <button
                          onClick={() => deleteFaq(index)}
                          className="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition-colors p-2"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                        <div className="grid gap-4">
                          <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Question</label>
                            <input
                              type="text"
                              value={faq.question}
                              onChange={(e) => updateFaq(index, 'question', e.target.value)}
                              className="w-full px-4 py-3 border rounded-xl focus:bg-white transition-all shadow-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Answer</label>
                            <textarea
                              value={faq.answer}
                              onChange={(e) => updateFaq(index, 'answer', e.target.value)}
                              rows={2}
                              className="w-full px-4 py-3 border rounded-xl focus:bg-white transition-all shadow-sm resize-none"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPageEditor;
